import React, { FormEvent, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { Link } from '@tanstack/react-location';
import { forgotPasswordRequest } from '../requests/session.request';

// Brand colors matching wefighttickets.com
const colors = {
  orange: '#fc6f23',
  orangeHover: '#d8531a',
  darkInk: '#191b1f',
  mutedGray: '#5c606b',
  border: '#ece8e2',
  cream: '#faf8f5',
  white: '#ffffff'
};

const styles = {
  pageBackground: {
    minHeight: '100vh',
    backgroundColor: colors.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  } as React.CSSProperties,
  card: {
    maxWidth: 440,
    width: '100%',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: 20,
    boxShadow: '0 30px 60px -42px rgba(0,0,0,0.15)',
    padding: 30
  } as React.CSSProperties,
  logo: {
    textAlign: 'center',
    marginBottom: 24
  } as React.CSSProperties,
  logoText: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.darkInk,
    margin: 0
  } as React.CSSProperties,
  logoAccent: {
    color: colors.orange
  } as React.CSSProperties,
  heading: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.darkInk,
    textAlign: 'center',
    marginBottom: 16
  } as React.CSSProperties,
  description: {
    fontSize: 14,
    color: colors.mutedGray,
    textAlign: 'center',
    marginBottom: 24
  } as React.CSSProperties,
  label: {
    fontSize: '.76rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.mutedGray,
    marginBottom: 8
  } as React.CSSProperties,
  input: {
    padding: '13px 14px',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    fontSize: 16,
    backgroundColor: colors.white
  } as React.CSSProperties,
  submitBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    fontWeight: 800,
    fontSize: 16,
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    marginTop: 8
  } as React.CSSProperties,
  backLink: {
    fontSize: 14,
    color: colors.orange,
    textDecoration: 'none'
  } as React.CSSProperties,
  successAlert: {
    backgroundColor: '#e8f5e9',
    borderColor: '#c8e6c9',
    color: '#2e7d32',
    borderRadius: 10,
    padding: '16px',
    marginBottom: 16
  } as React.CSSProperties
};

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPasswordRequest(email.trim());
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.pageBackground}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <h1 style={styles.logoText}>
            WE FIGHT <span style={styles.logoAccent}>TICKETS</span>
          </h1>
        </div>
        <h2 style={styles.heading}>Reset your password</h2>
        {submitted ? (
          <>
            <Alert style={styles.successAlert}>
              If an account with that email exists, a password reset link has been sent. Please
              check your inbox.
            </Alert>
            <div style={{ textAlign: 'center' }}>
              <Link to="/login" style={styles.backLink}>
                Back to login
              </Link>
            </div>
          </>
        ) : (
          <>
            <p style={styles.description}>
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>
            <Form onSubmit={onSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              <Form.Group className="mb-3" controlId="forgotEmail">
                <Form.Label style={styles.label}>Email</Form.Label>
                <Form.Control
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={styles.input}
                />
              </Form.Group>
              <Button type="submit" disabled={submitting} style={styles.submitBtn}>
                {submitting ? <Spinner animation="border" size="sm" /> : 'Send Reset Link'}
              </Button>
            </Form>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to="/login" style={styles.backLink}>
                Back to login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
